import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface User {
  id: number;
  nome: string;
  email: string;
  roles: string[];
  is_admin?: boolean;
}

interface AuthContextData {
  user: User | null;
  signed: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStorageData() {
      setLoading(true);
      
      try {
        const storedToken = localStorage.getItem('@CCPMIDIA:token');
        const storedUser = localStorage.getItem('@CCPMIDIA:user');

        if (storedToken && storedUser) {
          api.defaults.headers.Authorization = `Bearer ${storedToken}`;
          
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Log das informações para debug
          console.log('User loaded from storage:', parsedUser);
          console.log('is_admin property:', parsedUser.is_admin);
        }
      } catch (error) {
        console.error('Error loading auth data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadStorageData();
  }, []);

  async function login(email: string, password: string) {
    try {
      setLoading(true);
      
      const response = await api.post('/auth/login', { email, password });
      const { token, user } = response.data;
      
      // Garantir que user tem a propriedade is_admin
      if (typeof user.is_admin === 'undefined') {
        console.warn('is_admin property missing from user object, checking roles');
        
        // Tentar determinar is_admin a partir dos roles se não estiver explícito
        const isAdmin = user.roles && 
          Array.isArray(user.roles) && 
          user.roles.some((role: string) => role === 'ROLE_ADMIN' || role === 'admin');
        
        user.is_admin = isAdmin;
      }

      console.log('Login successful, user data:', user);
      
      localStorage.setItem('@CCPMIDIA:token', token);
      localStorage.setItem('@CCPMIDIA:user', JSON.stringify(user));

      api.defaults.headers.Authorization = `Bearer ${token}`;
      setUser(user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    localStorage.removeItem('@CCPMIDIA:token');
    localStorage.removeItem('@CCPMIDIA:user');
    setUser(null);
    delete api.defaults.headers.Authorization;
  }

  return (
    <AuthContext.Provider value={{ signed: !!user, user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
} 